import React from 'react'
import type {FlexProps} from '@chakra-ui/react'
import {Text, Flex, Tooltip} from '../../../utils/chakraMonomorphic'

import {formatDate} from '../../../utils'
import {CustomIcon} from '../../../components'

/**
 * The audience text, wrapped in a tooltip only when there are selections to
 * reveal. `tabIndex` makes the trigger reachable by keyboard, so the tooltip is
 * not mouse-only.
 */
const AudienceLabel: React.FC<{label: string; detail?: string | null}> = ({
  label,
  detail,
}) => {
  const text = (
    <Text
      fontSize='sm'
      fontWeight='medium'
      color='#747474'
      lineHeight='short'
      data-testid='micropost-audience-label'>
      {label}
    </Text>
  )

  if (!detail) return text

  return (
    <Tooltip label={detail} openDelay={300} maxW='20rem'>
      <Flex tabIndex={0} data-testid='micropost-audience-tooltip-trigger'>
        {text}
      </Flex>
    </Tooltip>
  )
}

export const ItemMeta: React.FC<
  {
    visibleDate: Date
    /**
     * Undefined hides the audience row entirely, reverting to the pre-AB#19976
     * date-only layout — used when the `enable-seasonality` ConfigCat flag is
     * off.
     */
    audienceLabel?: string
    /**
     * The audience's individual selections, shown only on hover. Null whenever
     * there is nothing to add beyond the label itself, in which case no tooltip
     * is attached at all.
     */
    audienceDetail?: string | null
    notificationSent?: boolean
  } & FlexProps
> = ({
  visibleDate,
  audienceLabel,
  audienceDetail,
  notificationSent,
  ...rest
}) => (
  <Flex {...rest} flexDirection='column'>
    <Flex alignItems='center'>
      <Text
        fontSize='sm'
        fontWeight='medium'
        color='#747474'
        lineHeight='short'>
        {formatDate(visibleDate, 'longString')}
      </Text>

      {audienceLabel && (
        <>
          <Text px={2} fontSize='sm' color='#747474' lineHeight='short'>
            |
          </Text>

          <CustomIcon
            width='1.2rem'
            name='audience'
            color='#747474'
            style={{marginRight: '.4rem'}}
          />
          <AudienceLabel label={audienceLabel} detail={audienceDetail} />
        </>
      )}
    </Flex>

    {notificationSent && (
      <Flex>
        <CustomIcon
          width='1.6rem'
          name='notification'
          style={{paddingRight: '.5vw'}}
        />
        <Text
          fontSize='sm'
          fontWeight='medium'
          color='#747474'
          lineHeight='short'>
          Push notification sent
        </Text>
      </Flex>
    )}
  </Flex>
)
